import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { 
  Calendar, ArrowLeft, User, Clock, Tag, Share2, 
  Facebook, Twitter, Linkedin, Copy, Check, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import ReactMarkdown from "react-markdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(response.data);
      
      // Fetch related posts from same category
      if (response.data.category) {
        const relatedResponse = await axios.get(`${API}/blog/posts?category=${response.data.category}&limit=3`);
        setRelatedPosts(
          relatedResponse.data.posts?.filter(p => p.slug !== slug).slice(0, 2) || []
        );
      }
    } catch (error) {
      console.error("Error fetching post:", error);
      if (error.response?.status === 404) {
        navigate("/tools", { replace: true });
        toast.error("Blog post not found");
      }
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = window.location.href;
  const shareTitle = post?.title || "";

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Link copied to clipboard!");
  };

  const shareOn = (platform) => {
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    };
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  // Calculate reading time
  const readingTime = post?.content 
    ? Math.ceil(post.content.split(/\s+/).length / 200)
    : 1;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Post Not Found</h1>
          <Link to="/tools" className="text-brand hover:underline">
            Return to Tools
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <>
        <title>{post.meta_title || post.title} | CompLy CMS</title>
        <meta name="description" content={post.meta_description || post.excerpt} />
        <meta property="og:title" content={post.meta_title || post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt} />
        {post.featured_image && <meta property="og:image" content={post.featured_image} />}
        <meta property="og:type" content="article" />
        <link rel="canonical" href={shareUrl} />
      </>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-b from-primary to-primary-dark text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link 
              to="/tools#resources-insights" 
              className="inline-flex items-center text-white/80 hover:text-white mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Resources & Insights
            </Link>
            
            <Badge className="bg-white/20 text-white mb-4">{post.category}</Badge>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-6">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-white/80">
              <span className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Marshalee Valentine
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {new Date(post.publish_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {readingTime} min read
              </span>
            </div>
          </div>
        </div>

        {/* Featured Image */}
        {post.featured_image && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
            <img
              src={post.featured_image}
              alt={post.title}
              className="w-full h-64 sm:h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        )}

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Main Content */}
            <article className="lg:col-span-9 prose prose-lg max-w-none">
              <ReactMarkdown
                components={{
                  h1: ({node, ...props}) => <h1 className="text-3xl font-heading font-bold mt-8 mb-4" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-2xl font-heading font-bold mt-8 mb-4" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-xl font-heading font-semibold mt-6 mb-3" {...props} />,
                  p: ({node, ...props}) => <p className="text-slate-700 leading-relaxed mb-4" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />,
                  li: ({node, ...props}) => <li className="text-slate-700" {...props} />,
                  a: ({node, ...props}) => <a className="text-brand hover:underline" {...props} />,
                  blockquote: ({node, ...props}) => (
                    <blockquote className="border-l-4 border-brand pl-4 italic text-slate-600 my-4" {...props} />
                  ),
                  strong: ({node, ...props}) => <strong className="font-semibold text-slate-900" {...props} />,
                  em: ({node, ...props}) => <em className="italic" {...props} />,
                }}
              >
                {post.content}
              </ReactMarkdown>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-3">
              {/* Share */}
              <div className="bg-slate-50 rounded-xl p-4 mb-6 sticky top-24">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Share2 className="h-4 w-4" />
                  Share this article
                </h4>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => shareOn('facebook')}
                    title="Share on Facebook"
                  >
                    <Facebook className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => shareOn('twitter')}
                    title="Share on Twitter"
                  >
                    <Twitter className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => shareOn('linkedin')}
                    title="Share on LinkedIn"
                  >
                    <Linkedin className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={copyLink}
                    title="Copy link"
                  >
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Category */}
              <div className="mb-6">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Category
                </h4>
                <Link 
                  to={`/tools#resources-insights`}
                  className="text-brand hover:underline"
                >
                  {post.category}
                </Link>
              </div>
            </aside>
          </div>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div className="border-t pt-12 mt-12">
              <h3 className="text-2xl font-heading font-bold mb-6">Related Articles</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedPosts.map((related) => (
                  <Link
                    key={related.id}
                    to={`/tools/resources/${related.slug}`}
                    className="group block bg-slate-50 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    {related.featured_image && (
                      <img
                        src={related.featured_image}
                        alt={related.title}
                        className="w-full h-40 object-cover"
                      />
                    )}
                    <div className="p-4">
                      <Badge variant="outline" className="mb-2">{related.category}</Badge>
                      <h4 className="font-semibold group-hover:text-brand transition-colors line-clamp-2">
                        {related.title}
                      </h4>
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {related.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="bg-brand/5 border border-brand/20 rounded-xl p-8 mt-12 text-center">
            <h3 className="text-2xl font-heading font-bold mb-3">
              Need Expert Guidance?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Our team of certified consultants can help you implement best practices 
              and achieve your compliance goals.
            </p>
            <Link to="/contact">
              <Button className="bg-brand hover:bg-brand-dark">
                Request a Consultation
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogDetail;
